import React, { useState, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import OfferHeader from './OfferHeader';
import OfferDetails from './OfferDetails';
import OfferModal from './OfferModal';
import { CompoundOffer } from '../../dtos';
import UpArrowIcon from '../../../../public/assets/icons/common/UpArrow';
import { logEvent } from '../../../../new-lib/analytics';

interface OfferCardProps {
  offer: CompoundOffer;
}
const OfferCard = ({ offer }: OfferCardProps) => {
  const { t } = useTranslation(['cityscape', 'common']);
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const router = useRouter();

  const handleOfferClick = () => {
    logEvent.click('click_offer_card', 'offer_card', 'offers', {
      cmpnd_id: offer?.id,
      cmpnd_name: offer?.name,
      area_name: offer?.area?.name,
      dev_id: offer?.developer?.id,
      dev_name: offer?.developer?.name
    });
    router.push(`/compound/${offer?.slug}`);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  const showMoreOffers = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    event.stopPropagation();
    logEvent.click('click_more_offers', 'offer_card', 'offers', {
      cmpnd_id: offer?.id,
      cmpnd_name: offer?.name,
      area_name: offer?.area?.name,
      dev_id: offer?.developer?.id,
      dev_name: offer?.developer?.name
    });
    setIsPopupOpen(!isPopupOpen);
  };

  const renderOfferDetails = useCallback(
    (offer: any, closeModal: boolean) => (
      <>
        <OfferHeader
          offer={offer}
          onClick={handleOfferClick}
          closeModal={closeModal}
        />
        <OfferDetails
          modalOpen={!closeModal}
          offer={offer.compound_offers[0]}
          index={0}
        />
      </>
    ),
    [offer, router.locale]
  );

  return (
    <CardContainer>
      {renderOfferDetails(offer, true)}
      {isMobile && isPopupOpen && (
        <>
          {offer.compound_offers
            .slice(1)
            .map((additionalOffer: any, index: number) => (
              <OfferDetails
                key={index}
                offer={additionalOffer}
                index={index + 1}
              />
            ))}
        </>
      )}
      {offer?.compound_offers?.length > 1 && (
        <div className="more-offers" onClick={e => showMoreOffers(e)}>
          <span>
            {isPopupOpen ? (
              <>
                {t('See Less', { ns: 'common' })}
                <UpArrowIcon className={`icon-arrow`} />
              </>
            ) : (
              <>
                {`${offer.compound_offers.length - 1} ${
                  offer.compound_offers.length > 2
                    ? t('more_offers')
                    : t('more_offer')
                }`}
                <UpArrowIcon className={`icon-arrow rotated`} />
              </>
            )}
          </span>
        </div>
      )}
      <OfferModal
        isOpen={isPopupOpen}
        handleClose={handleClosePopup}
        offer={offer}
        renderOfferDetails={renderOfferDetails}
      />
    </CardContainer>
  );
};

const CardContainer = styled.div`
  position: relative;
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 8px;
  border-radius: 12px;
  background: #fbfdff;
  box-shadow:
    0px 0px 4px 0px rgba(0, 0, 0, 0.04),
    0px 8px 16px 0px rgba(0, 0, 0, 0.08);

  .modal-content {
    max-height: 90vh;
    padding: 0px;
    border-radius: 12px;
    background: #fff;
    padding: 0px 16px;

    .modal-header {
      height: 52px;
      position: sticky;
      top: 0;
      z-index: 100;
      background-color: white;
      .close-btn-wrapper {
        align-self: last baseline;
        inset-inline-end: 0%;
        bottom: 8%;
        button {
          border-radius: 50%;
        }
      }
    }

    .offer-modal-content {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 4px;
      border-radius: 12px;
      background: #fbfdff;
    }
  }
  .more-offers-header {
    align-self: flex-start;
    color: #1a2028;
    font-family: materialSemiBold;
    font-size: 14px;
    line-height: 21px;
  }

  .more-offers {
    display: flex;
    align-self: center;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    span {
      display: flex;
      align-items: center;
      color: #1a2028;
      font-family: materialMedium;
      font-size: 12px;
      gap: 4px;

      .icon-arrow {
        &.rotated {
          transform: rotate(180deg);
        }
      }
    }

    &.modal {
      width: 100%;
      justify-content: center;
      padding: 16px;

      span {
        color: #1a2028;
        font-family: materialRegular;
        font-size: 12px;
      }
    }
  }
`;

export default OfferCard;
